if (!customElements.get('bokumo-sticky-cart')) {
  customElements.define('bokumo-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-submit]');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');
      this.productInfo = this.closest('product-info');
      if (!this.button || !this.productInfo) return;
      this.schedule = this.schedule.bind(this);
      this.activate = this.activate.bind(this);
      this.button.addEventListener('click', this.activate);
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule, { passive: true });
      window.addEventListener('pageshow', this.schedule);
      this.productInfo.addEventListener('change', this.schedule);
      this.mutations = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.mutations.observe(this.productInfo, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'value', 'hidden'],
      });
      this.footer = document.querySelector('footer.footer') || document.querySelector('footer');
      this.intersections = new IntersectionObserver(this.schedule);
      if (this.footer) this.intersections.observe(this.footer);
      this.formsEmbeds = new Map();
      this.formsMounts = new MutationObserver(() => this.syncFormsClearance());
      this.formsMounts.observe(document.body, { childList: true, subtree: true });
      customElements.whenDefined('shopify-forms-embed').then(() => {
        if (this.isConnected) this.syncFormsClearance();
      });
      this.schedule();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.activate);
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      window.removeEventListener('pageshow', this.schedule);
      this.productInfo?.removeEventListener('change', this.schedule);
      this.mutations?.disconnect();
      this.intersections?.disconnect();
      this.formsMounts?.disconnect();
      this.formsEmbeds?.forEach((style, host) => {
        host.removeAttribute('data-bokumo-sticky-clearance');
        host.style.removeProperty('--bokumo-sticky-cart-clearance');
        style.remove();
      });
      this.formsEmbeds?.clear();
      cancelAnimationFrame(this.frame);
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    source() {
      const form = document.getElementById(this.dataset.formId);
      const button = form?.querySelector('button[type="submit"]');
      if (!form || !button || !this.productInfo.contains(form)) return null;
      return { form, button };
    }

    sync() {
      const source = this.source();
      if (!source) {
        this.hidden = true;
        this.syncFormsClearance();
        return;
      }
      if (source.button !== this.observedButton) {
        if (this.observedButton) this.intersections.unobserve(this.observedButton);
        this.observedButton = source.button;
        this.intersections.observe(source.button);
      }
      const busy = source.button.classList.contains('loading');
      const disabled = source.button.disabled || source.button.getAttribute('aria-disabled') === 'true';
      this.button.disabled = disabled || busy;
      this.button.setAttribute('aria-busy', String(busy));
      this.label.textContent = source.button.querySelector('span:not(.hidden)')?.textContent.trim() || source.button.textContent.trim();
      const priceRoot = document.getElementById(`price-${this.dataset.sectionId}`);
      const sale = priceRoot?.querySelector('.price--on-sale');
      const price = priceRoot?.querySelector(sale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
      if (price) this.price.textContent = price.textContent.trim();
      const footerRect = this.footer?.getBoundingClientRect();
      const footerVisible = footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0;
      const sourceVisible = source.button.getClientRects().length > 0;
      this.hidden = !sourceVisible || source.button.getBoundingClientRect().bottom > 0 || Boolean(footerVisible);
      this.syncFormsClearance();
    }

    syncFormsClearance() {
      const clearance = this.hidden ? null : `${Math.max(0, Math.ceil(window.innerHeight - this.getBoundingClientRect().top + 12))}px`;
      document.querySelectorAll('shopify-forms-embed').forEach((host) => {
        if (!host.shadowRoot || this.formsEmbeds.has(host)) return;
        const style = document.createElement('style');
        style.textContent = ':host([data-bokumo-sticky-clearance]) [data-form-root="true"][data-current-step="tease"][data-position^="bottom-"] { bottom: var(--bokumo-sticky-cart-clearance, 0px) !important; }';
        host.shadowRoot.append(style);
        this.formsEmbeds.set(host, style);
      });
      this.formsEmbeds.forEach((style, host) => {
        if (!host.isConnected || clearance === null) {
          host.removeAttribute('data-bokumo-sticky-clearance');
          host.style.removeProperty('--bokumo-sticky-cart-clearance');
          if (!host.isConnected) {
            style.remove();
            this.formsEmbeds.delete(host);
          }
          return;
        }
        if (!host.hasAttribute('data-bokumo-sticky-clearance')) host.setAttribute('data-bokumo-sticky-clearance', '');
        if (host.style.getPropertyValue('--bokumo-sticky-cart-clearance') !== clearance) {
          host.style.setProperty('--bokumo-sticky-cart-clearance', clearance);
        }
      });
    }

    activate() {
      const source = this.source();
      if (!source || this.forwarding || source.button.disabled || source.button.getAttribute('aria-disabled') === 'true' || source.button.classList.contains('loading')) return;
      this.forwarding = true;
      this.button.disabled = true;
      source.button.click();
      this.sync();
      queueMicrotask(() => { this.forwarding = false; });
    }
  });
}
